/**
 * Video API — frontend (docs/VIDEO_UPLOADS.md). Uploading and tagging are
 * dark: until VIDEO_STATUS is 'live' those routes answer 404 to everyone but
 * admins, and the UI offers them only when useVideoEnabled() says so.
 * Watching isn't (V7): getTripVideos answers any member of the trip with the
 * clips they may watch.
 *
 * Calls resolve to the server's JSON body plus `status`, and never throw for
 * an HTTP error: the upload sheet needs the codes (quota_exceeded,
 * too_many_in_flight, …) to say what went wrong.
 */

import { API_BASE } from '../config/api';
import AuthService from './authService';

// A save the person is waiting on gives up after this long ('network'), so a
// hung request can't hold its sheet open for ever.
const SAVE_TIMEOUT_MS = 20000;

async function call(method, path, body, { timeoutMs = 0 } = {}) {
  const controller = timeoutMs > 0 && typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await fetch(`${API_BASE}/api/videos${path}`, {
      method,
      headers: AuthService.getJsonHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller?.signal
    });
    const json = await response.json().catch(() => ({}));
    // Timed out while the body was still arriving: not an answer.
    if (controller?.signal.aborted) return { success: false, status: 0, code: 'network', error: 'timeout' };
    return { ...json, status: response.status, success: response.ok && json.success !== false };
  } catch (error) {
    return { success: false, status: 0, code: 'network', error: error.message };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Clips this page session has reported watching (V7): each once.
const viewedThisSession = new Set();
// A play counts once it has run this long (VIDEO_PLAN D11).
export const VIEW_AFTER_MS = 3000;

/**
 * Counts what was actually watched (V7, D11): the time between consecutive
 * timeupdates while playing, so a seek — opening at a chapter, a chapter tap,
 * a scrub — adds nothing. Records the view once VIEW_AFTER_MS has played.
 * @returns {function} (currentTimeMs) => void — feed it every timeupdate
 */
export function createViewMeter(video) {
  let last = null;
  let watched = 0;
  let done = !video || video.is_owner;
  return (ms) => {
    if (done || !Number.isFinite(ms)) return;
    if (last != null) {
      const step = ms - last;
      // timeupdate fires ~4×/s while playing; a bigger jump is a seek.
      if (step > 0 && step < 1500) watched += step;
    }
    last = ms;
    if (watched >= VIEW_AFTER_MS) {
      done = true;
      VideoService.recordView(video);
    }
  };
}

const VideoService = {
  /**
   * Someone else's clip played for VIEW_AFTER_MS in the viewer or gallery:
   * counted on the server in batches (never a row per view), once a session
   * here and once per 30 minutes there. The uploader's own plays aren't sent.
   */
  recordView(video) {
    const id = Number(video?.video_id);
    if (!Number.isSafeInteger(id) || id <= 0 || video?.is_owner || viewedThisSession.has(id)) return;
    viewedThisSession.add(id);
    call('POST', `/${id}/view`).catch(() => {});
  },

  /**
   * A trip's clips for a member — their own in every state, co-travellers'
   * they may watch (`is_owner` says which) — shaped like trip photo pool items.
   * null when the request failed — not the same fact as "no videos", and a
   * caller must not act on it as one.
   */
  async getTripVideos(tripId) {
    const res = await call('GET', `/trip/${encodeURIComponent(tripId)}`);
    return res.success ? (res.videos || []) : null;
  },

  getQuota: () => call('GET', '/uploads/quota'),
  listOpenUploads: () => call('GET', '/uploads'),
  createUpload: (body) => call('POST', '/uploads', body),
  getUpload: (token, from) => call('GET', `/uploads/${token}${from ? `?from=${from}` : ''}`),
  completeUpload: (token) => call('POST', `/uploads/${token}/complete`),
  cancelUpload: (token) => call('DELETE', `/uploads/${token}`),
  // The same route deletes a finished clip (V4): renditions, original, recap links.
  deleteVideo: (token) => call('DELETE', `/uploads/${token}`),
  retryVideo: (token) => call('POST', `/uploads/${token}/retry`),
  // Who may watch the clip (V7): 'private' | 'friends' | 'public' → { visibility } or a code.
  setVisibility: (token, visibility) => call('PATCH', `/uploads/${token}`, { visibility }),
  // The clip's place tags (V6): the whole list in order → { tags, kind } or a code.
  // Replaces the whole list, so a retry after a timeout is safe.
  saveTags: (token, tags) => call('PUT', `/uploads/${token}/tags`, { tags }, { timeoutMs: SAVE_TIMEOUT_MS }),
  // What a clip on this trip can be tagged with: { days, results } (q searches near the trip).
  getTripPlaces: (tripId, q = '') => call('GET', `/trip/${encodeURIComponent(tripId)}/places${q ? `?q=${encodeURIComponent(q)}` : ''}`)
};

export default VideoService;
